import {
  Building,
  User2,
  Factory,
  Landmark,
  Ship,
  Banknote,
  Sprout,
  Gavel,
  Hammer,
  MoreHorizontal,
} from 'lucide-react';
import { FormField } from '../form-fields/FormField';
import { CardSelect, CardOption } from '../form-fields/CardSelect';
import { TextInput } from '../form-fields/TextInput';
import { UserInfoFormData } from '../hooks/useUserInfoForm';

interface Step3AccountTypeProps {
  formData: UserInfoFormData;
  loading: boolean;
  updateField: (field: keyof UserInfoFormData, value: string) => void;
  wiggleFields: Set<string>;
}

export function Step3AccountType({
  formData,
  loading,
  updateField,
  wiggleFields,
}: Step3AccountTypeProps) {
  const userTypeOptions: CardOption[] = [
    { value: 'private', label: 'Private', description: 'For personal use', icon: User2 },
    {
      value: 'business',
      label: 'Business',
      description: 'For companies and teams',
      icon: Building,
    },
  ];

  const companySegmentOptions: CardOption[] = [
    { value: 'Energy', label: 'Energy', icon: Factory },
    { value: 'Insurance', label: 'Insurance', icon: Landmark },
    { value: 'Logistics', label: 'Logistics', icon: Ship },
    { value: 'Finance', label: 'Finance', icon: Banknote },
    { value: 'Agriculture', label: 'Agriculture', icon: Sprout },
    { value: 'Government', label: 'Government', icon: Gavel },
    { value: 'Construction', label: 'Construction', icon: Hammer },
    { value: 'Other', label: 'Other', icon: MoreHorizontal },
  ];

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-lg font-medium text-theme-light-text-primary dark:text-theme-dark-text-primary mb-1">
          Account type
        </h2>
        <p className="text-sm text-theme-light-text-secondary dark:text-theme-dark-text-secondary">
          Choose what best describes you
        </p>
      </div>

      <div className="space-y-6">
        <div className={wiggleFields.has('userType') ? 'wiggle-animation' : ''}>
          <FormField label="Select one" htmlFor="userType" required>
            <CardSelect
              options={userTypeOptions}
              value={formData.userType}
              onChange={(val) => updateField('userType', val)}
              disabled={loading}
            />
          </FormField>
        </div>

        {formData.userType === 'business' && (
          <>
            <div className={wiggleFields.has('companyName') ? 'wiggle-animation' : ''}>
              <FormField label="Company Name" htmlFor="companyName" required>
                <TextInput
                  id="companyName"
                  name="companyName"
                  placeholder="Enter your company name"
                  value={formData.companyName}
                  onChange={(e) => updateField('companyName', e.target.value)}
                  disabled={loading}
                  required
                  icon={Building}
                />
              </FormField>
            </div>

            <div className={wiggleFields.has('companySegment') ? 'wiggle-animation' : ''}>
              <FormField label="Company Segment" htmlFor="companySegment" required>
                <div className="space-y-2">
                  <CardSelect
                    options={companySegmentOptions}
                    value={formData.companySegment}
                    onChange={(val) => updateField('companySegment', val)}
                    disabled={loading}
                  />
                  {formData.companySegment === 'Other' && (
                    <FormField
                      label="Please specify your industry (optional)"
                      htmlFor="companySegmentOther"
                      helpText="You can leave this blank if you prefer not to specify"
                    >
                      <TextInput
                        id="companySegmentOther"
                        name="companySegmentOther"
                        placeholder="Please specify your industry"
                        value={formData.companySegmentOther}
                        onChange={(e) => updateField('companySegmentOther', e.target.value)}
                        disabled={loading}
                      />
                    </FormField>
                  )}
                </div>
              </FormField>
            </div>
          </>
        )}

        {/* Role selection moved to Step 4 to avoid scrolling */}
      </div>
    </div>
  );
}
